import React, { useCallback, useContext, useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router";
import EncarteCard from "../../sharable/EncarteCard.jsx";
import { createSearchParams } from "react-router-dom";
import Button from "../../sharable/ActionButton.jsx";
import Encarte from "../../../OBJRep/Encarte.js";
import { devices } from "../../../Devices.js";
import Logo from "../../sharable/Logo.jsx";
import Loading from "../../sharable/Loading.jsx";
import { IonIcon } from "@ionic/react";
import { logOut, menuOutline } from "ionicons/icons";
import { cleanTokenCookie } from "../../../TokenCookie.js";
import PopUp from "../../sharable/PopUp.jsx";
import { requestValidationCodeNotification } from "../EmailValidation.jsx";
import { ModalPopUp } from "../../sharable/ModalPopup.jsx";
import { loadResource } from "../../../services/ResourcesService.js";
import PaginationButton from "../../sharable/PaginationButton.jsx";
import { formatDate } from "../../../services/Utils.js";
import Help from "../../sharable/Help.jsx";
import Headline from "../../sharable/Headline.jsx";
import Notification from "../../sharable/Notification.jsx";
import HomeLatestThemesPreview from "./HomeLatestThemesPreview.jsx";
import useCreateEncarte from "../../sharable/hooks/useCreateEncarte.js";
import { useDeviceType } from "../../sharable/hooks/useDeviceType.js";
import { useStartTrial } from "../../sharable/hooks/useStartTrial.js";
const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;
const Container = styled.div`
  width: 100vw;
  padding-bottom: 5.6rem;
  h1 {
    font-size: 2.4rem;
    font-weight: normal;
    margin-top: 3rem;
  }

  @media ${devices.tablet} {
    gap: 1rem;
  }
`;

const Header = styled.header`
  padding: 2rem 4.2rem;
`;

const Nav = styled.li`
  margin-top: 2rem;
  list-style: none;
  display: flex;
  gap: 3.6rem;
  text-transform: capitalize !important;
  @media ${devices.tablet} {
    display: none;
  }
`;

const NavItem = styled.ul`
  font-size: 1.8rem;
  cursor: pointer;
  @media ${devices.tablet} {
    font-size: 1.6rem;
  }
  &&:hover {
    color: #ae5f05;
    transition: all 300ms ease-in-out;
    @media ${devices.tablet} {
      color: #fff;
      font-weight: bold;
      border-bottom: 1px solid #fff;
    }
  }
`;

const Anchor = styled.a`
  all: unset;
`;

const Main = styled.main`
  padding: 0 10rem;
  gap: 5rem;
  margin-top: 7.2rem;
  h1 {
    text-transform: uppercase;
  }

  .welcome-subscription-end-date-wrapper {
    text-align: right;
  }

  h2 {
    font-weight: 500;
    text-align: right;
  }

  span {
    font-size: 1.2rem;
  }

  .past-due {
    color: #dd1c1a;
  }

  .active {
    color: #d97706;
  }
  p {
    font-size: 1.4rem;
    text-align: center;
    width: 100%;
  }
  @media ${devices.laptop} {
    padding: 5rem;
    h1 {
      text-align: center;
    }

    @media ${devices.tablet} {
      margin-top: 0;

      padding: 2.4rem;
      h1 {
        text-align: left;
      }
    }
  }
`;

const EncartesContainer = styled.ul`
  position: relative;
  margin-bottom: 3.6rem;
  display: grid;
  grid-template-columns: repeat(6, min-content);
  gap: 4.2rem;
  justify-content: center;
  ${({ $Models }) =>
    $Models &&
    css`
      grid-template-columns: repeat(5, min-content);
      justify-content: center;
      //gap: 3rem !important;
      z-index: 10;
    `}

  @media ${devices.laptop} {
    grid-template-columns: repeat(4, min-content);
  }
  @media ${devices.tablet} {
    gap: 2.8rem;
  }
  @media ${devices.mobileL} {
    grid-template-columns: repeat(3, min-content);
    gap: 2rem;
  }
`;

const ActionButtonsCardWrapper = styled.div`
  .action-buttons-wrapper {
    width: 100%;
  }
`;

const ActionButton = styled.button`
  all: unset;
  width: 100%;
  margin-top: 0.8rem;
  cursor: pointer;
  text-align: center;
  font-size: 1.2rem;
  border: 1px solid black;
  border-radius: 2px;
  padding: 0.4rem 0;
  font-weight: 600;
  &:hover {
    background-color: #d97706;
    border-color: transparent;
    color: #fff;
    transition: all 300ms ease-in-out;
  }
`;

let ENCARTE_PER_PAGE = 12;
export default function Home() {
  const navigate = useNavigate();

  const { user, token, activeAccount, accountStatus } = useOutletContext();

  const [showModal, setShowModal] = useState(undefined);

  const [maxEncarteCreationReached, setMaxEncarteCreationReached] =
    useState(false);
  const [encartePage, setCurrentEncartePage] = useState(undefined);
  const [notification, setNotification] = useState(undefined);
  const location = useLocation();
  const { createEncarte } = useCreateEncarte({ token, setNotification });
  const { startTrial } = useStartTrial({ token, user, setNotification });

  const deviceType = useDeviceType();
  if (deviceType === "mobile") ENCARTE_PER_PAGE = 6;
  if (deviceType === "laptop") ENCARTE_PER_PAGE = 12;
  const loadEncarteFirstPage = useEffect(() => {
    loadEncartePage();
  }, []);

  const loadEncartePage = useCallback(
    async (nextPage) => {
      const params = new URLSearchParams();
      params.append("pageNumber", nextPage ? nextPage : 0);
      params.append("pageSize", ENCARTE_PER_PAGE);
      params.append("sortDirection", "DESC");
      const url = `${BASE_URL}encarte-collection?${params.toString()}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: token,
        },
      });
      const newPage = await res.json();
      if (res.ok) {
        // Progressive loading: update state as each model loads
        let encartesSoFar = encartePage?.encartes
          ? [...encartePage.encartes]
          : [];
        for (const e of newPage.encartes) {
          const model = await loadResource(e.modelUrl, token);
          e.urlTmp = model.urlTmp;
          encartesSoFar.push(e);
          setCurrentEncartePage((prev) => ({
            ...(prev || newPage),
            encartes: [...encartesSoFar],
          }));
        }
        // Set the rest of the page info (totalPages, pageNumber, etc)
        setCurrentEncartePage((prev) => ({
          ...(prev || newPage),
          totalPages: newPage.totalPages,
          pageNumber: newPage.pageNumber,
          hasNextPage: newPage.hasNextPage,
        }));
      }
    },
    [encartePage, token],
  );
  const handleEncarteCreation = async () => {
    createEncarte(Encarte());
  };

  const handleEncarteDelete = async (id) => {
    const url = `${BASE_URL}encarte-collection/${id}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Authorization: token,
      },
    });

    const remainingEncartes = encartePage.encartes.filter((e) => e.id != id);
    encartePage.encartes = remainingEncartes;
    setCurrentEncartePage({ ...encartePage });
    if (response.ok) {
      setNotification({
        message: "Encarte removido com sucesso!",
        type: "success",
        autoHide: true,
        duration: 3000,
      });
    } else
      setNotification({
        message: "Não foi possível remover o encarte.",
        type: "error",
        autoHide: true,
        duration: 3000,
      });
  };
  const handleEncarteUpdate = async (id) => {
    navigate(`/editor?encarteId=${id}&update=true`);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("autoCreateEncarte") === "true") {
      handleEncarteCreation();
    }
  }, [location.search]);
  return (
    <Container className="flex direction-column">
      {notification && <Notification {...notification} />}

      {!user.accountVerified && (
        <Headline
          buttonText={"Receber código no meu email"}
          text={"Precisamos que você valide sua conta :)."}
          fontSize={"1.2rem"}
          buttonAction={async () => {
            const res = await requestValidationCodeNotification(user.email);
            if (res.ok) {
              navigate("/verificacao-email?email=" + user.email);
            } else if (res.status === 429) {
              setNotification({
                message: "Aguarde antes de solicitar outro código.",
                type: "info",
                autoHide: true,
                duration: 4000,
              });
            } else {
              setNotification({
                message:
                  "Não foi possível enviar o código para o seu email. Contate o suporte.",
                type: "error",
                autoHide: true,
                duration: 5000,
              });
            }
          }}
        />
      )}
      {user && (
        <>
          <PopUp
            text={"Você atingiu a quantidade máxima de encartes do dia!"}
            showPopUp={maxEncarteCreationReached}
            setShowPopUp={setMaxEncarteCreationReached}
            buttonText={"Ok"}
          />
          {showModal === "subscription" && (
            <ModalPopUp
              buttonAction={() =>
                navigate(activeAccount ? "/pagamentos" : "/planos")
              }
              buttonText="Ver planos"
              title="Assine agora e potencialize suas vendas!"
              text="Com o ofertas express você cria suas ofertas em minutos. É
                  rápido e prático!"
              showActionButton={true}
            />
          )}

          {showModal === "emailValidation" && (
            <ModalPopUp
              showActionButton={true}
              buttonAction={async () => {
                const res = await requestValidationCodeNotification(user.email);
                if (res.ok) navigate("/verificacao-email");
              }}
              text={
                "Precisamos saber se esse email te pertence! Levará menos de 1 minuto ;)"
              }
              title={"Você precisa verificar o seu email antes de continuar"}
              buttonText={"Clique para receber o código de verificação!"}
            />
          )}

          {showModal === "trial" && (
            <ModalPopUp
              showActionButton={true}
              buttonAction={startTrial}
              text={
                "Inicie seu período de teste grátis de 1 dia e tenha acesso a todas as funcionalidades."
              }
              title={"Conheça o ofertasexpress!"}
              buttonText={"Testar agora!"}
            />
          )}

          <Header className="flex space-between align">
            <MobileMenu />
            <Logo />

            <Nav>{NavJSX()}</Nav>

            <Button
              text={"criar encarte"}
              action={async () => {
                if (accountStatus?.onTrial) {
                  await handleEncarteCreation();
                  return;
                }
                if (!user.accountVerified) {
                  setShowModal("emailValidation");
                  return;
                }
                if (
                  accountStatus.lastPaymentId == null &&
                  !user.trialEnd &&
                  !accountStatus.accessExpiryDate
                ) {
                  setShowModal("trial");
                  return;
                }
                if (!activeAccount) {
                  setShowModal("subscription");
                  return;
                }

                await handleEncarteCreation();
              }}
            />
          </Header>
          <HomeLatestThemesPreview />
          <Main className="flex direction-column">
            <div className="flex direction-column">
              <div className="welcome-subscription-end-date-wrapper">
                {encartePage?.encartes.length > 0 ? (
                  <h2>Bem vindo de volta, {user.name} ;)!</h2>
                ) : (
                  <h2>Bem vindo, {user.name} ;)!</h2>
                )}
                {!activeAccount &&
                  accountStatus?.lastPaymentId === null &&
                  !accountStatus?.onTrial &&
                  user.trialEnd !== null && (
                    <span className="past-due">
                      {" "}
                      Seu período de teste finalizou dia{" "}
                      {formatDate(user.trialEnd)}.{" "}
                    </span>
                  )}
                {accountStatus?.onTrial && (
                  <span className="active">
                    {" "}
                    Seu período de teste termina dia {formatDate(user.trialEnd)}
                    .{" "}
                  </span>
                )}
                {accountStatus?.accessExpiryDate
                  ? (() => {
                      console.log(
                        "Expiry date:",
                        accountStatus?.accessExpiryDate,
                      );
                      const expiry = new Date(accountStatus?.accessExpiryDate);
                      const now = new Date();
                      if (
                        accountStatus?.lastPaymentId != null &&
                        expiry < now
                      ) {
                        return (
                          <span className="past-due">
                            Seu acesso venceu dia {formatDate(expiry)}.{" "}
                          </span>
                        );
                      } else {
                        return (
                          <span className="active">
                            Seu acesso vence dia {formatDate(expiry)}.
                          </span>
                        );
                      }
                    })()
                  : ""}
              </div>
              <h1>Meus encartes</h1>
            </div>
            <EncartesContainer key={user}>
              {encartePage?.encartes.length > 0
                ? encartePage.encartes.map((e) => {
                    return (
                      <ActionButtonsCardWrapper>
                        <EncarteCard
                          onDelete={() => {
                            handleEncarteDelete(e);
                          }}
                          onClick={() => {}}
                          name={e.name}
                          url={e.urlTmp}
                          encarte={e}
                          isIncludedInPlan={true}
                        />
                        <div className="flex action-buttons-wrapper direction-column">
                          <ActionButton
                            onClick={() => handleEncarteDelete(e.id)}
                          >
                            Remover
                          </ActionButton>
                          <ActionButton
                            onClick={() => handleEncarteUpdate(e.id)}
                          >
                            Editar
                          </ActionButton>
                        </div>
                      </ActionButtonsCardWrapper>
                    );
                  })
                : !encartePage && (
                    <Loading txt={"Carregando seus encartes..."} />
                  )}
            </EncartesContainer>
            {encartePage?.encartes.length === 0 && (
              <p>Você não possui nenhum encarte ainda :(</p>
            )}

            {encartePage && (
              <PaginationButton
                color="#fff"
                text="Carregar mais encartes"
                callback={loadEncartePage}
                totalPages={encartePage.totalPages}
                currentPage={encartePage.pageNumber}
                hasNextPage={encartePage.hasNextPage}
              />
            )}
          </Main>
        </>
      )}
      <Help />
    </Container>
  );
}

const MenuContainer = styled.div`
  text-transform: capitalize !important;
  display: none;

  .menu-icon-wrapper {
    font-size: 1.2rem;
    flex-direction: row-reverse;
    gap: 0.6rem;
  }
  .menu-icon {
    font-size: 2rem;
  }
  @media ${devices.tablet} {
    display: inline-block;
  }
`;

const ShowMenuAnimation = keyframes`
0% {
  width: 0px
}
100% {
  width: 120px;
}
`;

const LateralMenu = styled.nav`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  animation: ${ShowMenuAnimation} 300ms linear;
  background-color: red;
  color: #fff;
  background-color: #ae5f05;
  padding: 1.4rem;
  gap: 1rem;
  height: 100vh;
  z-index: 10;
`;

const MobileMenu = () => {
  const [showMenu, setShowMenu] = useState(false);

  const handleClickOutside = (e) => {
    const target = e.target;
    if (!target.closest(".mob-menu")) setShowMenu(false);
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  return (
    <MenuContainer className="mob-menu" onClick={() => setShowMenu(true)}>
      <div className="menu-icon-wrapper flex align">
        <span>Menu</span> <IonIcon icon={menuOutline} className="menu-icon" />
      </div>
      {showMenu ? (
        <LateralMenu>
          <NavJSX />
        </LateralMenu>
      ) : (
        ""
      )}
    </MenuContainer>
  );
};

function NavJSX() {
  const navigate = useNavigate();
  const { user, activeAccount } = useOutletContext();
  return (
    <>
      <>
        <NavItem
          onClick={() =>
            navigate(
              activeAccount
                ? "/pagamentos"
                : user.accountVerified
                  ? "/planos"
                  : "/verificar-email",
            )
          }
        >
          {activeAccount
            ? "Meus pagamentos"
            : user.accountVerified
              ? "Ver planos"
              : "Verificar email"}
        </NavItem>

        <NavItem>
          {" "}
          <Anchor
            href="https://www.youtube.com/channel/UCDcSC98INa1A-su_jnxvT5Q"
            target="_blank"
            rel="noopener noreferrer"
          >
            Aprenda aqui
          </Anchor>
        </NavItem>
      </>
      <NavItem
        onClick={() => {
          cleanTokenCookie();
          navigate("/entrar");
        }}
      >
        <div className="flex align min-gap">
          desconectar <IonIcon icon={logOut} />
        </div>
      </NavItem>{" "}
      {user.authority === "ADMIN" && (
        <NavItem
          onClick={() => {
            navigate("/user-dashboard");
          }}
        >
          Meus clientes
        </NavItem>
      )}
    </>
  );
}
